import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import {
  getCustomerEntitlements,
  getCustomerFeatureOverrides,
  getFeatures,
} from '../api/entitlementsApi';
import { getCustomerInvitations, getCustomerUsers } from '../api/authApi';
import { getTenantModuleAccessSummary } from '../api/mafertappModulesApi';
import { colorTokens } from '../app/designTokens';
import { DataTable } from './DataTable';
import { LoadingSkeleton } from './LoadingSkeleton';
import { SectionCard } from './SectionCard';
import { StatusBadge } from './StatusBadge';
import { TableToolbar } from './TableToolbar';
import {
  formatCustomerRole,
  formatModuleLabel,
  formatModuleSource,
} from '../utils/customerPortalLabels';

const { semantic } = colorTokens;

const SHOW_TECHNICAL_MATRIX = process.env.REACT_APP_SHOW_TECHNICAL_PERMISSION_MATRIX === 'true';

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

export function CustomerEntitlementsTab({ customerId, customer }) {
  const { t } = useTranslation(['customers', 'features', 'statuses', 'common']);
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [modules, setModules] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const tenantId = customer?.tenant_summary?.id;

  const load = useCallback(async () => {
    setLoading(true);
    setMessage('');

    try {
      const requests = [
        getCustomerUsers(customerId),
        getCustomerInvitations(customerId),
      ];

      if (tenantId) {
        requests.push(getTenantModuleAccessSummary(tenantId));
      }

      if (SHOW_TECHNICAL_MATRIX) {
        requests.push(getCustomerEntitlements(customerId));
        requests.push(getFeatures({ per_page: 200, scope: 'legacy', include_legacy: true }));
        requests.push(getCustomerFeatureOverrides(customerId));
      }

      const results = await Promise.allSettled(requests);

      if (results[0]?.status === 'fulfilled') setUsers(results[0].value.data || []);
      if (results[1]?.status === 'fulfilled') setInvitations(results[1].value.data || []);

      if (tenantId && results[2]?.status === 'fulfilled') {
        const summary = results[2].value;
        const active = [
          ...(summary.core_modules || []),
          ...(summary.active_paid_modules || summary.paid_modules || []).filter((item) => item.enabled !== false),
          ...(summary.support_modules || []).filter((item) => item.enabled !== false),
          ...(summary.future_override_modules || []).filter((item) => item.enabled),
        ];
        setModules(active);
      } else if (customer?.registration_summary?.modules_selected) {
        const selected = customer.registration_summary.modules_selected;
        setModules(Array.isArray(selected) ? selected.map((code) => ({ module_code: code, public_label: code })) : []);
      }

      if (SHOW_TECHNICAL_MATRIX && results[3]?.status === 'fulfilled') {
        setEntitlements(results[3].value.data || []);
      }
    } catch {
      setMessage(t('customers:access.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [customer?.registration_summary?.modules_selected, customerId, t, tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const userColumns = useMemo(() => [
    {
      id: 'name',
      label: t('common:name'),
      render: (user) => user.name || '—',
    },
    {
      id: 'email',
      label: t('common:email'),
      render: (user) => user.email || '—',
    },
    {
      id: 'role',
      label: t('common:role'),
      render: (user) => formatCustomerRole(user.role) || '—',
    },
    {
      id: 'status',
      label: t('common:status'),
      render: (user) => <StatusBadge status={user.membership_status} category="membership" size="Sm" />,
    },
    {
      id: 'invited_at',
      label: t('customers:invited_at'),
      render: (user) => formatDate(user.invited_at),
    },
  ], [t]);

  const invitationColumns = useMemo(() => [
    {
      id: 'email',
      label: t('common:email'),
      render: (invitation) => invitation.email || '—',
    },
    {
      id: 'role',
      label: t('common:role'),
      render: (invitation) => formatCustomerRole(invitation.role) || invitation.role,
    },
    {
      id: 'status',
      label: t('common:status'),
      render: (invitation) => <StatusBadge status={invitation.status} category="invitation" size="Sm" />,
    },
    {
      id: 'expires',
      label: t('customers:expires'),
      render: (invitation) => formatDate(invitation.expires_at),
    },
  ], [t]);

  const moduleColumns = useMemo(() => [
    {
      id: 'feature',
      label: t('features:feature'),
      render: (item) => (
        formatModuleLabel(item, item.module_code || item.code)
          || item.customer_name
          || item.public_label
          || item.module_code
          || item
      ),
    },
    {
      id: 'source',
      label: t('features:source'),
      render: (item) => formatModuleSource(item.source || (item.is_core ? 'core' : 'registration')),
    },
  ], [t]);

  const entitlementColumns = useMemo(() => [
    {
      id: 'feature',
      label: t('features:feature'),
      render: (item) => item.feature_name || item.feature_code,
    },
    {
      id: 'enabled',
      label: t('features:enabled'),
      render: (item) => (item.enabled ? t('statuses:yes') : t('statuses:no')),
    },
    {
      id: 'source',
      label: t('features:source'),
      render: (item) => item.source,
    },
  ], [t]);

  if (loading) {
    return <LoadingSkeleton type="Detail" />;
  }

  const portalStatus = customer?.billing_summary?.is_trial
    ? t('customers:access.portal_trial')
    : t('customers:access.portal_active');

  return (
    <Box>
      {message && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}

      <SectionCard variant="Default" title={t('customers:access.portal_status')}>
        <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
          {portalStatus}
        </Typography>
      </SectionCard>

      <SectionCard variant="Default" noPadding sx={{ mb: 3 }}>
        <TableToolbar title={t('customers:access.users_title')} />
        <DataTable
          type="Default"
          embedded
          columns={userColumns}
          rows={users}
          emptyMessage={t('customers:no_users')}
        />
      </SectionCard>

      <SectionCard variant="Default" noPadding sx={{ mb: 3 }}>
        <TableToolbar title={t('customers:invitations_title')} />
        <DataTable
          type="Default"
          embedded
          columns={invitationColumns}
          rows={invitations}
          emptyMessage={t('customers:no_invitations')}
        />
      </SectionCard>

      <SectionCard variant="Default" noPadding sx={{ mb: 2 }}>
        <TableToolbar title={t('customers:access.active_modules')} />
        <DataTable
          type="Default"
          embedded
          columns={moduleColumns}
          rows={modules}
          getRowKey={(item) => item.module_code || item.code || item.id || String(item)}
          emptyMessage={t('customers:access.no_modules')}
        />
      </SectionCard>

      {SHOW_TECHNICAL_MATRIX ? (
        <SectionCard variant="Default">
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              bgcolor: 'transparent',
              backgroundImage: 'none',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: semantic.text.muted }} />}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: semantic.text.primary }}>
                {t('customers:access.technical_permissions')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pb: 0 }}>
              <DataTable
                type="Technical"
                embedded
                columns={entitlementColumns}
                rows={entitlements}
                getRowKey={(item) => item.feature_code}
                emptyMessage={t('common:no_data')}
              />
            </AccordionDetails>
          </Accordion>
        </SectionCard>
      ) : (
        <Alert severity="info">{t('customers:access.technical_hidden')}</Alert>
      )}
    </Box>
  );
}
